import { ReactNode } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useConversations } from '@/hooks/useConversations';

interface NavItem {
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  href: string;
  match: (path: string) => boolean;
  badge?: number;
}

const TOP_LEVEL_PATHS = new Set([
  '/', '/students', '/library', '/retencao', '/finances', '/chat', '/my-workout', '/profile',
]);

function isTopLevelPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return TOP_LEVEL_PATHS.has(normalized);
}

export function DesktopShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { profile, signOut } = useAuth();
  const { totalUnread } = useConversations(profile?.id ?? '');

  const items: NavItem[] = [
    {
      label: 'Dashboard',
      icon: 'grid-outline',
      href: '/(personal)/(tabs)',
      match: p => p === '/' || p === '/(personal)' || p === '/(personal)/(tabs)' || p === '/(personal)/(tabs)/' || p.endsWith('/(tabs)/index'),
    },
    {
      label: 'Alunos',
      icon: 'people-outline',
      href: '/(personal)/(tabs)/students',
      match: p => p.includes('/students') && !p.includes('/students/'),
    },
    {
      label: 'Exercícios',
      icon: 'barbell-outline',
      href: '/(personal)/(tabs)/library',
      match: p => p.includes('/library'),
    },
    {
      label: 'Retenção',
      icon: 'pulse-outline',
      href: '/(personal)/retencao',
      match: p => p.includes('/retencao'),
    },
    {
      label: 'Financeiro',
      icon: 'wallet-outline',
      href: '/(personal)/finances',
      match: p => p.includes('/finances'),
    },
    {
      label: 'Chat',
      icon: 'chatbubbles-outline',
      href: '/(personal)/chat',
      match: p => p.includes('/chat'),
      badge: totalUnread,
    },
    {
      label: 'Meu treino',
      icon: 'fitness-outline',
      href: '/(personal)/my-workout',
      match: p => p.includes('/my-workout'),
    },
  ];

  const initials = (profile?.full_name ?? profile?.email ?? '?')
    .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <View style={styles.shell}>

      {/* Sidebar */}
      <View style={styles.sidebar}>
        <View style={styles.brand}>
          <Text style={styles.brandText}><Text style={{ color: '#C8102E' }}>ARA</Text>FIT</Text>
          <Text style={styles.brandSub}>Personal</Text>
        </View>

        <ScrollView style={styles.navList} contentContainerStyle={{ paddingVertical: 8 }}>
          {items.map(item => {
            const active = item.match(pathname);
            return (
              <TouchableOpacity
                key={item.label}
                style={[styles.navItem, active && styles.navItemActive]}
                onPress={() => router.push(item.href as never)}
                activeOpacity={0.7}
              >
                <Ionicons name={item.icon} size={18} color={active ? '#C8102E' : '#888'} />
                <Text style={[styles.navLabel, active && styles.navLabelActive]}>{item.label}</Text>
                {!!item.badge && item.badge > 0 && (
                  <View style={styles.navBadge}>
                    <Text style={styles.navBadgeText}>{item.badge > 9 ? '9+' : String(item.badge)}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Footer: usuário */}
        <View style={styles.userBox}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{initials}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.userName} numberOfLines={1}>
              {profile?.full_name ?? 'Personal'}
            </Text>
            <TouchableOpacity onPress={() => router.push('/(personal)/profile' as never)}>
              <Text style={styles.userLink}>Meu perfil</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={signOut} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="log-out-outline" size={20} color="#888" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Main */}
      <View style={styles.main}>
        <View style={styles.mainInner}>
          {!isTopLevelPath(pathname) && (
            <View style={styles.topbar}>
              <TouchableOpacity
                style={styles.backBtn}
                onPress={() => (router.canGoBack() ? router.back() : router.push('/(personal)/(tabs)' as never))}
                activeOpacity={0.7}
              >
                <Ionicons name="chevron-back" size={16} color="#1A1A1A" />
                <Text style={styles.backText}>Voltar</Text>
              </TouchableOpacity>
            </View>
          )}
          <View style={{ flex: 1 }}>
            {children}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    minHeight: '100%' as unknown as number,
  },

  sidebar: {
    width: 240,
    backgroundColor: '#0F0F0F',
    borderRightWidth: 1,
    borderRightColor: '#1E1E1E',
    flexDirection: 'column',
  },

  brand: { padding: 20, borderBottomWidth: 1, borderBottomColor: '#1E1E1E' },
  brandText: { color: '#fff', fontSize: 18, fontWeight: '800', letterSpacing: 1.5 },
  brandSub: { color: '#666', fontSize: 11, marginTop: 2 },

  navList: { flex: 1 },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginHorizontal: 8,
    borderRadius: 8,
    marginBottom: 2,
  },
  navItemActive: { backgroundColor: 'rgba(200, 16, 46, 0.12)' },
  navLabel: { flex: 1, color: '#888', fontSize: 13, fontWeight: '600' },
  navLabelActive: { color: '#C8102E' },
  navBadge: {
    backgroundColor: '#C8102E',
    borderRadius: 10,
    minWidth: 20, height: 20,
    paddingHorizontal: 5,
    justifyContent: 'center', alignItems: 'center',
  },
  navBadgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  userBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#1E1E1E',
  },
  avatar: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: '#1E1E1E',
    justifyContent: 'center', alignItems: 'center',
  },
  avatarText: { color: '#fff', fontWeight: '700', fontSize: 13 },
  userName: { color: '#fff', fontSize: 13, fontWeight: '600' },
  userLink: { color: '#888', fontSize: 11, marginTop: 2 },

  main: { flex: 1, overflow: 'hidden', alignItems: 'center' },
  mainInner: { flex: 1, width: '100%', maxWidth: 1400 },

  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 4,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#EEE',
  },
  backText: { fontSize: 12, fontWeight: '600', color: '#1A1A1A' },
});
